import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import {
  ArrowLeft, Save, User, Mail, Phone, Building, Shield, Eye, EyeOff
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { userService, plantService } from '../../services/api';
import { extractArrayData } from '../../utils/helpers';
import LoadingSpinner from '../Common/LoadingSpinner';

const buildUserSchema = (isEdit) =>
  Yup.object().shape({
    name: Yup.string()
      .min(2, 'Name must be at least 2 characters')
      .required('Name is required'),
    email: Yup.string()
      .email('Invalid email address')
      .required('Email is required'),
    password: isEdit
      ? Yup.string().min(6, 'Password must be at least 6 characters')
      : Yup.string()
        .min(6, 'Password must be at least 6 characters')
        .required('Password is required'),
    confirmPassword: isEdit
      ? Yup.string()
        .oneOf([Yup.ref('password'), null], 'Passwords must match')
      : Yup.string()
        .oneOf([Yup.ref('password'), null], 'Passwords must match')
        .required('Please confirm your password'),
    role: Yup.string().required('Role is required'),
    // userType: Yup.string().required('User type is required'),
    plant: Yup.string().when('role', (role, schema) => {
      return role === 'plantAdmin' || role === 'employee'
        ? schema.required('Plant is required for this role')
        : schema;
    }),
    phone: Yup.string().matches(/^[0-9+\-\s()]*$/, 'Invalid phone number format'),
    isActive: Yup.boolean(),
  });

const UserForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Fetch user for editing
  const {
    data: userData,
    isLoading: isLoadingUser,
    error: userError
  } = useQuery(
    ['user', id],
    () => userService.getUserById(id),
    {
      enabled: !!id,
      staleTime: 5 * 60 * 1000
    }
  );

  const user = userData?.data;
  console.log(user)

  // Fetch plants for dropdown
  const { data: plantsData, isLoading: isLoadingPlants } = useQuery(
    ['plants'],
    () => plantService.getPlants(),
    {
      staleTime: 10 * 60 * 1000
    }
  );

  const plants = extractArrayData(plantsData);

  // Create/Update user mutation
  const mutation = useMutation(
    (data) => id ? userService.updateUser(id, data) : userService.createUser(data),
    {
      onSuccess: () => {
        toast.success(id ? 'User updated successfully' : 'User created successfully');
        queryClient.invalidateQueries('users');
        navigate('/users');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || `Failed to ${id ? 'update' : 'create'} user`);
      }
    }
  );

  // Initial values for form
  const getInitialValues = () => {
    if (user) {
      return {
        name: user.name || '',
        email: user.email || '',
        password: '',
        confirmPassword: '',
        role: user.role || '',
        userType: user.userType || '',
        plant: user.plant?._id || '',
        phone: user.phone || '',
        isActive: user.isActive !== undefined ? user.isActive : true
      };
    }
    return {
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      role: '',
      userType: '',
      plant: '',
      phone: '',
      isActive: true
    };
  };

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting }) => {
    setIsSubmitting(true);
    try {
      const formData = { ...values };

      // Remove password fields if they're empty (for updates)
      if (id && !formData.password) {
        delete formData.password;
        delete formData.confirmPassword;
      }

      await mutation.mutateAsync(formData);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setSubmitting(false);
      setIsSubmitting(false);
    }
  };

  if (isLoadingUser) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner text="Loading user..." />
      </div>
    );
  }

  if (userError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900">Error loading user</h3>
          <p className="mt-1 text-sm text-gray-500">{userError.message}</p>
          <button
            onClick={() => navigate('/users')}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Users
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/users')}
            className="mr-4 inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {id ? 'Edit User' : 'Create User'}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              {id ? 'Update user information' : 'Add a new user account'}
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">User Details</h3>
        </div>
        <div className="px-6 py-4">
          <Formik
            initialValues={getInitialValues()}
            validationSchema={buildUserSchema(!!id)}
            onSubmit={handleSubmit}
            enableReinitialize={true}
          >
            {({ values, errors, touched, isSubmitting, setFieldValue }) => (
              <Form className="space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                      User Name *
                    </label>
                    <div className="mt-1 relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                      <Field
                        type="text"
                        name="name"
                        id="name"
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter User Name"
                      />
                    </div>
                    <ErrorMessage name="name" component="p" className="text-red-500 text-sm mt-1" />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      Email Address *
                    </label>
                    <div className="mt-1 relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-gray-400" />
                      </div>
                      <Field
                        type="email"
                        name="email"
                        id="email"
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter email address"
                      />
                    </div>
                    <ErrorMessage name="email" component="p" className="text-red-500 text-sm mt-1" />
                  </div>
                </div>

                {/* Password Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                      Password {!id && '*'}
                    </label>
                    <div className="mt-1 relative">
                      <Field name="password">
                        {({ field, meta }) => (
                          <>
                            <input
                              {...field}
                              type={showPassword ? "text" : "password"}
                              id="password"
                              className="block w-full px-3 pr-10 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                              placeholder={id ? "Leave blank to keep current password" : "Enter password"}
                              onChange={(e) => {
                                field.onChange(e);
                              }}
                            />
                            <button
                              type="button"
                              className="absolute inset-y-0 right-0 pr-3 flex items-center"
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? (
                                <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                              ) : (
                                <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                              )}
                            </button>
                          </>
                        )}
                      </Field>
                    </div>
                    <ErrorMessage name="password" component="p" className="text-red-500 text-sm mt-1" />
                    {id && (
                      <p className="text-sm text-gray-500 mt-1">Leave blank to keep current password</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                      Confirm Password {!id && '*'}
                    </label>
                    <div className="mt-1 relative">
                      <Field name="confirmPassword">
                        {({ field, meta }) => (
                          <>
                            <input
                              {...field}
                              type={showConfirmPassword ? "text" : "password"}
                              id="confirmPassword"
                              className="block w-full px-3 pr-10 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Confirm password"
                            />
                            <button
                              type="button"
                              className="absolute inset-y-0 right-0 pr-3 flex items-center"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            >
                              {showConfirmPassword ? (
                                <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                              ) : (
                                <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                              )}
                            </button>
                          </>
                        )}
                      </Field>
                    </div>
                    <ErrorMessage name="confirmPassword" component="p" className="text-red-500 text-sm mt-1" />
                  </div>
                </div>

                {/* Role and User Type */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                      Role *
                    </label>
                    <div className="mt-1 relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Shield className="h-5 w-5 text-gray-400" />
                      </div>
                      <Field
                        as="select"
                        name="role"
                        id="role"
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select role</option>
                        <option value="superAdmin">Super Admin</option>
                        <option value="admin">Admin</option>
                        <option value="plantAdmin">Plant Admin</option>
                        <option value="employee">Employee</option>
                      </Field>
                    </div>
                    <ErrorMessage name="role" component="p" className="text-red-500 text-sm mt-1" />
                  </div>

                  {/* <div>
                    <label htmlFor="userType" className="block text-sm font-medium text-gray-700">
                      User Type *
                    </label>
                    <Field
                      as="select"
                      name="userType"
                      id="userType"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select user type</option>
                      <option value="internal">Internal</option>
                      <option value="external">External</option>
                      <option value="contractor">Contractor</option>
                    </Field>
                    <ErrorMessage name="userType" component="p" className="text-red-500 text-sm mt-1" />
                  </div> */}
                </div>

                {/* Plant Assignment */}
                <div>
                  <label htmlFor="plant" className="block text-sm font-medium text-gray-700">
                    Plant {(values.role === 'plantAdmin' || values.role === 'employee') && '*'}
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Building className="h-5 w-5 text-gray-400" />
                    </div>
                    <Field
                      as="select"
                      name="plant"
                      id="plant"
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      disabled={isLoadingPlants}
                    >
                      <option value="">Select plant</option>
                      {Array.isArray(plants) && plants.map((plant) => (
                        <option key={plant._id} value={plant._id}>
                          {plant.name} - {plant.location}
                        </option>
                      ))}
                    </Field>
                    {isLoadingPlants && (
                      <p className="text-sm text-gray-500 mt-1">Loading plants...</p>
                    )}
                    <ErrorMessage name="plant" component="p" className="text-red-500 text-sm mt-1" />
                  </div>
                </div>

                {/* Contact Information */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                    Phone Number
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Phone className="h-5 w-5 text-gray-400" />
                    </div>
                    <Field
                      type="tel"
                      name="phone"
                      id="phone"
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter phone number"
                    />
                  </div>
                  <ErrorMessage name="phone" component="p" className="text-red-500 text-sm mt-1" />
                </div>

                {/* Status */}
                <div>
                  <div className="flex items-center">
                    <Field
                      type="checkbox"
                      name="isActive"
                      id="isActive"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                      Active Account
                    </label>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Inactive users cannot log in to the system
                  </p>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => navigate('/users')}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {isSubmitting ? 'Saving...' : (id ? 'Update' : 'Create')}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default UserForm; 